import type { ComponentType, JSXElementConstructor, LazyExoticComponent } from 'react';
import React, { useEffect, useState } from 'react';
import { isLazy } from 'react-is';

/**
 * Used for chaining two event handlers together.
 *
 * @param first - The first handler to call.
 * @param second optional - The second handler to call.
 * @returns A new handler that calls the first and then the second (if provided).
 */
export const chainEvent = <El, E extends React.SyntheticEvent<El>>(
  first: (e: E) => unknown,
  second: ((e: E) => unknown) | null | undefined
): ((e: E) => void) => {
  return e => {
    first(e);
    if (second) {
      second(e);
    }
  };
};

const lazyRegistry = new WeakMap<JSXElementConstructor<any>, Array<(status: number) => void>>();

interface LazyExoticPreloadableComponent<T extends ComponentType<any>>
  extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

/**
 * Creates a `React.lazy` component wrapped in a proxy and stores it in a registry, so we can track
 * its initialization status. This can be used together with the `useLazyStatus` hook, since neither
 * `<Suspense>` nor `React.lazy` provide any interface or callbacks of their own for this purpose.
 *
 * Also exposes a `preload()` function on the registered component, which loads the component's code
 * without needing it to be rendered - for example in mouseenter handlers.
 *
 * Note that this relies on React internals, so we should revisit when we update React.
 */
export function registerLazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticPreloadableComponent<T> {
  const component = new Proxy(React.lazy(factory), {
    get(obj, prop) {
      const value = Reflect.get(obj, prop);
      // The component isn't re-rendered every time the status updates, but `<Suspense>` will read
      // the current status value, so we can use that as a signal
      if (prop === '_payload' && typeof value._status === 'number') {
        const callbacks = lazyRegistry.get(component);
        callbacks?.forEach(callback => callback(value._status));
      }
      return value;
    },
  }) as LazyExoticPreloadableComponent<T>;

  component.preload = factory;

  lazyRegistry.set(component, []);
  return component;
}

/**
 * Registers a callback that will be invoked whenever the given component type (registered through
 * `registerLazy`) changes its initialization status.
 * @returns True if the callback was successfully registered.
 */
export function registerLazyCallback<T extends JSXElementConstructor<any>>(
  componentType: T,
  callback: (status: number) => void
): boolean {
  const registration = lazyRegistry.get(componentType);
  if (!registration || registration.includes(callback)) return false;

  registration.push(callback);
  return true;
}

/**
 * Unregisters an initialization status callback from the given component type (registered through
 * `registerLazy`).
 * @returns True if the callback was successfully unregistered.
 */
export function unregisterLazyCallback<T extends JSXElementConstructor<any>>(
  componentType: T,
  callback: (status: number) => void
): boolean {
  const registration = lazyRegistry.get(componentType);
  if (!registration) return false;

  const callbackIndex = registration.indexOf(callback);
  if (callbackIndex === -1) return false;

  registration.splice(callbackIndex, 1);
  return true;
}

/**
 * Hook to track the initialization of a potential `React.lazy` element. Requires the component to
 * have been defined using `registerLazy`.
 * @returns
 * - `null` if the node isn't a `React.lazy` element
 * - `-1` if the element is uninitialized (`<Suspense>` shows fallback)
 * - `0` if the element is initializing (`<Suspense>` shows fallback)
 * - `1` if the element is successfully initialized (`<Suspense>` shows element)
 * - `2` if the element failed to initialize (error is thrown)
 */
export const useLazyStatus = (maybeLazy?: React.ReactNode) => {
  const [status, setStatus] = useState<number | null>(null);

  useEffect(() => {
    if (!isLazy(maybeLazy)) {
      setStatus(null);
      return;
    }

    const componentType = (maybeLazy as any).type;

    registerLazyCallback(componentType, setStatus);
    return () => {
      unregisterLazyCallback(componentType, setStatus);
    };
  }, [maybeLazy]);

  return status;
};

/**
 * Preloads a lazy component if compatible.
 */
export async function preloadIfLazy(maybeLazyPreloadable?: React.ReactNode): Promise<boolean> {
  const fn = (maybeLazyPreloadable as any)?.type?.preload;

  if (typeof fn !== 'function') return false;

  await fn();
  return true;
}

export const STOP_PROPAGATION = (e: React.SyntheticEvent) => e.stopPropagation();
